import React, { useState } from 'react';

const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Email validation
    if (!email.trim()) {
      setError('Email address is required');
      setLoading(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (email.length > 100) {
      setError('Email address is too long');
      setLoading(false);
      return;
    }

    try {
      if (!email.trim()) {
        setError('Please enter your email address');
        setLoading(false);
        return;
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';
      
      // First check if email exists
      const checkResponse = await fetch(`${apiBaseUrl}/CommonChannel/Check_UserAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          User_Email: email,
        }),
      });

      if (!checkResponse.ok) {
        throw new Error('Failed to verify email');
      }

      const userData = await checkResponse.json();

      if (!userData || !userData.user_Email) {
        setError('Email address not found. Please check and try again.');
        setLoading(false);
        return;
      }

      // Send forgot password email using MessageChannel API
      // Backend will fetch reset URL from App_Email_Configuration.Additional_Attchement_Link
      // and construct full URL with email parameter
      const emailParams = {
        emailTo: email,
        emailFrom: '',
        emailBody: '',
        emailSubject: '',
        attachment: 'N',
        userPhoneNumber: '',
        location: 'YugalKunj',
        appContent: {
          userName: `${userData.user_First_Name || ''} ${userData.user_Last_Name || ''}`.trim(),
          email: email,
          programName: '',
          appSchedule: '',
          attendee: '',
          date: '',
          orderType: '',
          orderNo: '',
          paymentMode: '',
          amount: '',
          phone: '',
          pickupPerson: '',
          pickupPersonContact: '',
          instructions: '',
          itemData: '',
          pickUpDate: '',
          pickUpTime: '',
          paymentType: '',
          billingAddress: '',
          address: '',
          pickupOrReceiver: '',
          orderReady: '',
          orderReadyLastLine: '',
          headingDate: '',
          headingTime: '',
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString()
        }
      };

      console.log('Sending password reset email to:', email);
      console.log('Email params:', emailParams);

      // Use exact case-sensitive values that match database configuration
      const sendEmailResponse = await fetch(
        `${apiBaseUrl}/messageChannel/SendEmailMessage?appName=Class&templateName=ClassForgetPassword&orgId=3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(emailParams),
        }
      );

      console.log('Email response status:', sendEmailResponse.status);

      if (!sendEmailResponse.ok) {
        const errorText = await sendEmailResponse.text();
        console.error('Email send failed - Status:', sendEmailResponse.status);
        console.error('Email send failed - Response:', errorText);
        
        // More specific error messages
        if (sendEmailResponse.status === 500) {
          setError('Server error: Email configuration may be missing. Please contact support.');
        } else if (sendEmailResponse.status === 404) {
          setError('Email service not found. Please contact support.');
        } else {
          setError(`Failed to send password reset email (Error ${sendEmailResponse.status}). Please try again or contact support.`);
        }
        setLoading(false);
        return;
      }

      // If response is OK (200), email was sent successfully
      // Backend returns empty string on success
      console.log('Password reset email sent successfully');
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slideInUp">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password</h2>
          <p className="text-gray-600 text-sm">Enter your email address and we'll send you a link to reset your password.</p>
        </div>

        {success ? (
          <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-green-800 font-semibold">Email Sent!</p>
                <p className="text-green-700 text-sm">Please check your email for the password reset link.</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-base text-gray-900"
                placeholder="your.email@example.com"
                disabled={loading}
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-green-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-green-700 transition-all duration-200 disabled:opacity-50 shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
